import { useQuery } from "@tanstack/react-query";
import {
  CACHE_KEYS,
  CATEGORIES_DEFAULT_BG_COLOR,
  CATEGORIES_DEFAULT_ICON,
  CATEGORIES_DEFAULT_TEXT_COLOR,
  ETransactionType,
  SOURCES_DEFAULT_BG_COLOR,
  SOURCES_DEFAULT_ICON,
  SOURCES_DEFAULT_TEXT_COLOR,
} from "../helpers/constants";
import { searchTransactions } from "../api/transactions";
import { useCallback, useMemo } from "react";
import { TRANSACTION_STATUS_PENDING } from "../constants/transaction-status";
import { compact, find, map, merge, sortBy, uniq } from "lodash-es";
import useMainFilter from "./useMainFilter";
import { getCategories } from "src/api/categories";
import { getSources } from "src/api/sources";
import { filterTransactions } from "src/helpers/utils";

const REFETCH_MINUTES = 10;
const REFETCH_INTERVAL = REFETCH_MINUTES * 1000 * 60;

export default function useAllTransactions() {
  const { mainFilter } = useMainFilter();

  const {
    data: customizedCategories,
    isLoading: isLoadingCustomizedCategories,
    isRefetching: isRefetchingCustomizedCategories,
  } = useQuery({
    queryFn: async () => await getCategories(),
    queryKey: [CACHE_KEYS.ALL_CATEGORIES],
    enabled: true,
  });

  const {
    data: customizedSources,
    isLoading: isLoadingCustomizedSources,
    isRefetching: isRefetchingCustomizedSources,
  } = useQuery({
    queryFn: async () => await getSources(),
    queryKey: [CACHE_KEYS.ALL_SOURCES],
    enabled: true,
  });

  const {
    data: allTransactions,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: [CACHE_KEYS.ALL_TRANSACTIONS],
    queryFn: () => searchTransactions({ fieldsToSearch: {} }),
    refetchInterval: REFETCH_INTERVAL,
    enabled: true, //!!mainFilter.account,
  });

  const allPendingTransactions = useMemo(() => {
    return filterTransactions(allTransactions?.data || [], {
      shared: false,
      archived: false,
      deleted: false,
      status: TRANSACTION_STATUS_PENDING,
    });
  }, [allTransactions?.data]);

  // Filtrado de las transacciones activas y no eliminadas
  const allActiveTransactions = useMemo(() => {
    // if (!mainFilter?.account) return [];
    return (
      allTransactions?.data?.filter(
        ({
          archived,
          shared,
          deleted,
        }: {
          archived: boolean;
          shared: boolean;
          deleted: boolean;
        }) => !archived && !shared && !deleted
      ) || []
    );
  }, [allTransactions?.data, mainFilter?.account, isLoading, isRefetching]);

  // Función para obtener una transacción por su ID
  const getTransactionById = ({ transactionId }: { transactionId: string }) =>
    find(allTransactions?.data || [], { invoice: transactionId });

  // Filtrado de transacciones pendientes de pagar
  const allTransactionsPendingToPay = useMemo(() => {
    if (!mainFilter?.account) return [];

    return allActiveTransactions.filter(
      (transaction: any) =>
        transaction?.status === TRANSACTION_STATUS_PENDING &&
        transaction.type === ETransactionType.EXPENSE
    );
  }, [allActiveTransactions]);

  // Filtrado de transacciones pendientes de cobrar
  const allTransactionsPendingToCollect = useMemo(() => {
    if (!mainFilter?.account) return [];

    return allActiveTransactions.filter(
      (transaction: any) =>
        transaction?.status === TRANSACTION_STATUS_PENDING &&
        transaction.type === ETransactionType.INCOME
    );
  }, [allActiveTransactions]);

  // Filtrado de transacciones recibidas
  const allReceivedTransactions = useMemo(() => {
    return (
      allTransactions?.data?.filter(
        ({ shared, deleted }: { shared: boolean; deleted: boolean }) => shared
      ) || []
    );
  }, [allTransactions?.data]);

  const allClientProviders = useMemo(
    () => uniq(compact(map(allTransactions?.data, "payTo"))),
    [allTransactions?.data, mainFilter?.account]
  );

  const allClientProvidersObject = useMemo(
    () =>
      sortBy(
        map(allClientProviders, (clientProvider) => ({
          value: clientProvider,
          label: clientProvider,
        })),
        "label"
      ),
    [allClientProviders]
  );

  const allCategories = useMemo(
    () => uniq(compact(map(allTransactions?.data || [], "category"))),
    [allTransactions?.data]
  );

  const allCategoriesObject = useMemo(
    () =>
      sortBy(
        map(allCategories, (category) => ({
          value: category,
          label: category,
        })),
        "label"
      ),
    [allCategories]
  );

  const getCategoryCustomSettings = useCallback(
    (category: string) => {
      const res = find(customizedCategories?.data, { category });

      return merge(
        {},
        {
          bg: CATEGORIES_DEFAULT_BG_COLOR,
          color: CATEGORIES_DEFAULT_TEXT_COLOR,
          icon: CATEGORIES_DEFAULT_ICON,
        },
        {
          ...res?.settings,
        }
      );
    },
    [isLoadingCustomizedCategories, customizedCategories]
  );

  const categoryExists = (category: string) => {
    return allCategories?.includes(category);
  };

  const allSources = useMemo(
    () => uniq(compact(map(allTransactions?.data || [], "payTo"))),
    [allTransactions?.data]
  );

  const allSourcesObject = useMemo(
    () =>
      sortBy(
        map(allSources, (source) => ({
          value: source,
          label: source,
        })),
        "label"
      ),
    [allSources]
  );

  const allDescriptions = useMemo(
    () => uniq(compact(map(allTransactions?.data || [], "description"))),
    [allTransactions?.data]
  );

  const allDescriptionsObject = useMemo(
    () =>
      sortBy(
        map(allDescriptions, (description) => ({
          value: description,
          label: description,
        })),
        "label"
      ),
    [allDescriptions]
  );

  const getSourceCustomSettings = useCallback(
    (source: string) => {
      const res = find(customizedSources?.data, { source });

      return merge(
        {},
        {
          bg: SOURCES_DEFAULT_BG_COLOR,
          color: SOURCES_DEFAULT_TEXT_COLOR,
          icon: SOURCES_DEFAULT_ICON,
          notes: [],
          contact: [],
          picture: null,
        },
        {
          ...res?.settings,
        }
      );
    },
    [isLoadingCustomizedSources, customizedCategories]
  );

  const sourceExists = (source: string) => {
    return allSources?.includes(source);
  };

  return {
    allTransactions: allTransactions?.data,
    allActiveTransactions,
    isLoadingAllTransactions: !!isLoading,
    allTransactionsPendingToCollect,
    allTransactionsPendingToPay,
    allReceivedTransactions,
    getTransactionById,
    isRefetchingAllTransactions: !!isRefetching,
    allClientProviders,
    allClientProvidersObject,
    allCategories,
    allCategoriesObject,
    isLoadingCustomizedCategories,
    isRefetchingCustomizedCategories,
    customizedCategories: customizedCategories?.data,
    getCategoryCustomSettings,
    categoryExists,
    allSources,
    allSourcesObject,
    customizedSources: customizedSources?.data,
    isLoadingCustomizedSources,
    isRefetchingCustomizedSources,
    getSourceCustomSettings,
    sourceExists,
    allDescriptions,
    allDescriptionsObject,
    allPendingTransactions,
  };
}
